import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, ArrowRight, Play } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const countryFlags = {
  DE: '🇩🇪', NL: '🇳🇱', IT: '🇮🇹', FR: '🇫🇷', PL: '🇵🇱',
  ES: '🇪🇸', PT: '🇵🇹', BE: '🇧🇪', SE: '🇸🇪', FI: '🇫🇮'
};

export const SessionCard = ({ session, variant = 'default' }) => {
  const isFree = session.session_type === 'free_session';
  const seatsFilled = session.seats_filled || 0;
  const maxSeats = session.max_seats || 100;
  const fillPercentage = Math.min((seatsFilled / maxSeats) * 100, 100);
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/session/${session.id}`}
        data-testid={`session-card-compact-${session.id}`}
        className="group flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-[#0AA06A]/30 hover:shadow-md transition-all"
      >
        <div className="flag-emoji text-2xl">
          {countryFlags[session.country_code] || '🏳️'}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-[#002A76] text-sm truncate group-hover:text-[#0AA06A] transition-colors">
            {session.title}
          </h4>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span className="flex items-center gap-1">
              <Calendar size={12} />
              {formatDate(session.date)}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {session.time}
            </span>
          </div>
        </div>
        <Badge className={isFree ? 'badge-free text-white' : 'badge-paid text-white'}>
          {isFree ? 'Free' : `₹${session.price}`}
        </Badge>
      </Link>
    );
  }

  return (
    <Link
      to={`/session/${session.id}`}
      data-testid={`session-card-${session.id}`}
      className="group session-card bg-white rounded-2xl border border-gray-100 overflow-hidden card-hover"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-[#002A76]/10 to-[#0AA06A]/10 overflow-hidden">
        {session.thumbnail ? (
          <img 
            src={session.thumbnail} 
            alt={session.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <div className="flag-emoji text-5xl mb-2">
                {countryFlags[session.country_code] || '🏳️'}
              </div>
              <span className="text-[#002A76]/60 font-medium text-sm">Live Session</span>
            </div>
          </div>
        )}
        
        {/* Play button overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
            <Play size={20} className="text-[#002A76] ml-1" fill="currentColor" />
          </div>
        </div>

        {/* Badge */}
        <div className="absolute top-3 right-3">
          <Badge className={`${isFree ? 'badge-free' : 'badge-paid'} text-white text-xs px-3 py-1`}>
            {isFree ? 'Free Session' : `Masterclass • ₹${session.price}`}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-bold text-[#002A76] text-lg mb-2 line-clamp-2 group-hover:text-[#0AA06A] transition-colors">
          {session.title}
        </h3>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {session.description}
        </p>

        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-4">
          <span className="flex items-center gap-1.5">
            <Calendar size={14} className="text-[#0AA06A]" />
            {formatDate(session.date)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock size={14} className="text-[#0AA06A]" />
            {session.time}
          </span>
        </div>

        {/* Seats progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span className="flex items-center gap-1">
              <Users size={12} />
              {seatsFilled} registered
            </span>
            <span>{maxSeats - seatsFilled} seats left</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full progress-bar rounded-full"
              style={{ width: `${fillPercentage}%` }}
            />
          </div>
        </div>

        {/* CTA */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">{session.duration}</span>
          <span className="flex items-center text-[#002A76] font-semibold text-sm group-hover:text-[#0AA06A] transition-colors">
            Register Now
            <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default SessionCard;
